import "./App.css";
import {
  DEFAULT_SIDEBAR_WIDTH,
  DRAG_LINK,
  DRAG_MOVE,
  DRAG_RESIZE_END,
  DRAG_RESIZE_START,
  zoomModeInfo,
  ZOOM_MODE_DAY,
} from "./gantt/helpers/Const";
import React, { PureComponent, ReactNode } from "react";
import { addHours, differenceInHours, subHours, addDays, isEqual, subDays, differenceInDays, getHours, startOfDay } from "date-fns";
import {
  getChildrenMinimumDurationInHours,
  getLinkedTaskIdsRecursive,
  getLongestDependencyChainDurationBackwardsInHours,
  getLongestDependencyChainDurationForwardsInHours,
  getSubtasksRecursive,
  updateUpdateDictWithLinkedTasks,
} from "./gantt/helpers/GraphUtils";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Header from "./gantt/components/header/Headers";
import TimeLineHub from "./gantt/components/timeline/TimeLineHub";
import { connect } from "react-redux";
import memoizeOne from "memoize-one";
import { hierarchicalDataForViewsSelector } from "./rematch/selectors/selectors";
import { ModalBackdrop, ModalContainer } from "./gantt/components/common/StyledComponents";
import { areListsEqual, snapMouseMoveBasedOnZoomMode } from "./gantt/helpers/MiscUtils";
import { LINKER_DIAMETER } from "./gantt/components/viewport/DataTask";
import { EditItemForm } from "./gantt/components/common/EditItemForm";
import { AddItemForm } from "./gantt/components/common/AddItemForm";
import { NOTIFY } from "./gantt/components/common/NotificationMessage";
import { findWorkingDayAdjustedTimeRange } from "./gantt/helpers/DateHelpers";
import { ensureStartAndEnd } from "./backend/helpers/dateHelpers";
import { withTranslation, WithTranslation } from "react-i18next";
import { ConfigurationForm } from "./gantt/components/common/ConfigurationForm";
import { Dispatch, iRootState } from "./rematch/store";
import { Action } from "@rematch/core";
import { DisplaySettings } from "./rematch/interfaces/states/IViewSettingsState";
import { IHierarchyItem } from "./backend/interfaces/IItem";
import { IObjectTypeConfigurationItem, IViewConfigurationItem } from "./backend/interfaces/configuration/IConfiguration";
import { IMoveOrResizeManyPayload } from "./rematch/interfaces/payloads/itemReducers";
import { withMsal } from "@azure/msal-react";
import { WithMsalProps } from "@azure/msal-react/dist/components/withMsal";
import { TokenForm } from "./gantt/components/common/TokenForm";
import { UIConfigForm } from "./gantt/components/common/UIConfigForm";

interface AppProps extends WithTranslation, WithMsalProps {
  allData: IHierarchyItem[];
  startDate: Date;
  endDate: Date;
  viewData: IHierarchyItem[][];
  viewConfigurations: IViewConfigurationItem[];
  loading: boolean;
  displaySettings: DisplaySettings;
  zoomMode: string;
  dayClicked: Date;
  modalContents: ReactNode;
  modalStyles: object;
  showNewTaskBox: boolean;
  showTaskEditBox: boolean;
  showConfigurationForm: boolean;
  showTokenForm: boolean;
  showUIConfigForm: boolean;

  moveOrResizeMany: (updateDict: IMoveOrResizeManyPayload) => Action<IMoveOrResizeManyPayload,void>;
  toggleLink: (linkFromId: number,linkToId: number) =>  Action<{ linkFromId: number; linkToId: number },void>;
  showMessage: (message: string, messageLevel: number) => Action<unknown,void>;
  hideModal: () => Action<void,void>;
  refreshItemsAsync: () => Action<void,void>;
  getObjectTypeConfig?: (objectTypeId: number) => IObjectTypeConfigurationItem,
  clearDayClickedState: () => void;
}

interface AppState {
  dragTaskId: number;
  dragType: string;
  mouseXDelta: number;
  mouseYDelta: number;
  viewPortWidth: number;
  modalContents: ReactNode;
  sideBarWidth: number;
}

interface AppSettings {
  allowParentResize: boolean;
}

class App extends PureComponent<AppProps, AppState> {

  mouseXStart: number;
  mouseYStart: number;
  nextStartDate: Date;
  prevStartDate: Date;
  prevEndDate: Date;
  settings: AppSettings;
  timer: NodeJS.Timeout;

  constructor(props) {
    super(props);

    this.mouseXStart = 0;
    this.mouseYStart = 0;
    this.prevStartDate = null;
    this.nextStartDate = null;

    const viewPortWidth = window.innerWidth - DEFAULT_SIDEBAR_WIDTH; //re-calculated properly by onResize event right at the start.

    // think about how to handle settings.    
    this.settings = {
      allowParentResize: true,
    };
    this.state = {
      dragTaskId: null,
      dragType: null,
      mouseXDelta: 0,
      mouseYDelta: 0,
      viewPortWidth: viewPortWidth,
      modalContents: null,
      sideBarWidth: null,
    };

    this.timer = setInterval(() => {
      this.refreshItems();
    }, 5000);
  }

  refreshItems = () => {
    if(!this.props.loading) {
      this.props.refreshItemsAsync();
    }
  }

  reRenderHeader = memoizeOne((startDate: Date, endDate: Date, hourWidth: number, zoomMode: string, taskHeight: number): JSX.Element => {
    const headerProps = {
      startDate: startDate,
      endDate: endDate,
      hourWidth: hourWidth,
      zoomMode: zoomMode,
      taskHeight: taskHeight,
    };
    return <Header {...headerProps} />;
  }, areListsEqual);

  onHorizontalResize = (totalWidth: number) => {
    this.setState({
      viewPortWidth: totalWidth,
    });
  };

  getHourWidth = (): number => {
    let hourWidth = zoomModeInfo[this.props.zoomMode].preliminaryHourWidth as number;
    let diffInHours = differenceInHours(this.props.endDate, this.props.startDate);
    diffInHours += 2 * zoomModeInfo[this.props.zoomMode].extraContextDays * 24;
    diffInHours += 24; //Add time from 00:00 to 23:59
    const requiredWidth = diffInHours * hourWidth;
    const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
    let viewPortWidth = this.state.viewPortWidth;
    if(isFirefox) {
      viewPortWidth -= 17; //For some reason Firefox doesn't calculate scrollbar size
    }
    if (requiredWidth < viewPortWidth) {
      hourWidth = viewPortWidth / diffInHours;
    }
    return hourWidth;
  };

  onTaskListSizing = (delta: number) => {
    this.setState((prevState) => {
      let newWidth = prevState.sideBarWidth - delta;
      newWidth = Math.max(140, Math.min(newWidth, window.innerWidth - 100));
      return { sideBarWidth: newWidth };
    });
  };

  getParentResizes = (viewIndex: number, task: IHierarchyItem, maxStartTime: Date, minEndTime: Date): IMoveOrResizeManyPayload => {

    let updateDict: IMoveOrResizeManyPayload = {};

    if (task.parentId != null) {
      const parent = this.props.viewData[viewIndex][task.parentId];
      updateDict = this.getParentResizes(viewIndex, parent, maxStartTime, minEndTime);
    }
    if (task.start > maxStartTime || task.end < minEndTime) {
      updateDict[task.id] = {
        itemId: task.id,
        newStart: new Date(Math.min(Number(maxStartTime), Number(task.start))),
        newEnd: new Date(Math.max(Number(minEndTime), Number(task.end))),
      };
    }
    return updateDict;
  };

  getTaskAndDependentsMoves = (viewIndex: number, baseTask: IHierarchyItem, newStart: Date, newEnd: Date): IMoveOrResizeManyPayload => {
    let updateDict: IMoveOrResizeManyPayload = {};
    if (baseTask.parentId != null) {
      const ownDuration = differenceInHours(baseTask.end, baseTask.start);
      const dependentsRequiredDuration =
        getLongestDependencyChainDurationForwardsInHours(this.props.viewData[viewIndex], baseTask) - ownDuration;
      const dependeesRequiredDuration =
        getLongestDependencyChainDurationBackwardsInHours(this.props.viewData[viewIndex], baseTask) - ownDuration;

      const parent = this.props.viewData[viewIndex][baseTask.parentId];

      if (this.settings.allowParentResize) {
        // Add this option to settings.
        const maxStartTime = subHours(newStart, dependeesRequiredDuration);
        const minEndTime = addHours(newEnd, dependentsRequiredDuration);
        updateDict = this.getParentResizes(viewIndex, parent, maxStartTime, minEndTime);
      } else {
        // Ensure that the move is not so great that the moved task's dependent/dependee siblings
        // Would leak out of the parent's start/end limits
        newEnd = new Date(Math.min(Number(parent.end), Number(addHours(newEnd, dependentsRequiredDuration))));
        newStart = new Date(Math.max(Number(parent.start), Number(subHours(newStart, dependeesRequiredDuration))));
      }
    }

    updateDict[baseTask.id] = {
      itemId: baseTask.id,
      newStart: newStart,
      newEnd: newEnd,
    };

    const subTasksToMove = getSubtasksRecursive(this.props.viewData[viewIndex], baseTask, new Set());
    if (subTasksToMove.size > 0) {
      updateDict = this.updateUpdateDictWithChildTasks(subTasksToMove, newEnd, newStart, updateDict);
    }
    updateDict = updateUpdateDictWithLinkedTasks(this.props.viewData[viewIndex], baseTask, updateDict);
    return updateDict;
  };

  updateUpdateDictWithChildTasks = (subTasksToMove: Set<IHierarchyItem>, newEnd: Date, newStart: Date, updateDict: IMoveOrResizeManyPayload): IMoveOrResizeManyPayload => {
    for (const childTask of subTasksToMove) {
      const objectTypeConfig        = this.props.getObjectTypeConfig(childTask.objectType);
      const [validStart, validEnd] = ensureStartAndEnd(childTask.start, childTask.end, startOfDay(Date.now()), childTask.duration, objectTypeConfig.durationUnit);
      const childDuration = differenceInHours(validEnd, validStart);
      let newStartChild = validStart;
      let newEndChild = validEnd;
      let originalMoveDirectionForwards: boolean;
      if (newEnd < validEnd) {
        originalMoveDirectionForwards = false;
        newEndChild = newEnd;
        newStartChild = subHours(newEndChild, childDuration);
      }
      if (newStart > validStart) {
        originalMoveDirectionForwards = true;
        newStartChild = newStart;
        newEndChild = addHours(newStartChild, childDuration);
      }
      if (this.props.displaySettings.adjustForWeekendAndHolidays) {
        const adjustedTimes = findWorkingDayAdjustedTimeRange(childTask, newStartChild, newEndChild, originalMoveDirectionForwards);
        newStartChild = adjustedTimes.newStart;
        newEndChild = adjustedTimes.newEnd;
      }
      updateDict[childTask.id] = {
        itemId: childTask.id,
        newStart: new Date(Math.max(Number(newStartChild), Number(newStart))),
        newEnd: new Date(Math.min(Number(newEndChild), Number(newEnd))),
      };
    }
    return updateDict;
  };

  moveTaskAndDependents = (taskId: number, viewIndex: number, moveHours: number) => {
    const task = this.props.viewData[viewIndex][taskId];
    const objectTypeConfig        = this.props.getObjectTypeConfig(task.objectType);
    const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
    const updateDict = this.getTaskAndDependentsMoves(
      viewIndex,
      task,
      addHours(validStart, moveHours),
      addHours(validEnd, moveHours)
    );
    this.props.moveOrResizeMany(updateDict);
  };

  moveMouse = (e: MouseEvent) => {
    this.setState(() => {
      return {
        mouseXDelta: e.clientX - this.mouseXStart,
        mouseYDelta: e.clientY - this.mouseYStart,
      };
    });
  };

  clearDragData = () => {
    this.setState(() => {
      return {
        dragTaskId: null,
        dragType: null,
        mouseXDelta: 0,
        mouseYDelta: 0,
      };
    });
    document.removeEventListener("mousemove", this.moveMouse);
  };

  onStartDrag = (e: React.MouseEvent<HTMLElement>, taskId: number, dragType: string) => {
    if (dragType === DRAG_LINK) {
      // Since the arrow drawing starts from the middle of the linker element and the cursor can be at
      // any point over the element, we find the offset from the midpoint.
      const elemBounds = e.currentTarget.getBoundingClientRect();
      const xOffset = LINKER_DIAMETER / 2 - (e.clientX - elemBounds.left);
      const yOffset = LINKER_DIAMETER / 2 - (e.clientY - elemBounds.top);
      this.mouseXStart = e.clientX + xOffset;
      this.mouseYStart = e.clientY + yOffset;
    } else {
      this.mouseXStart = e.clientX;
      this.mouseYStart = e.clientY;
    }
    document.addEventListener("mousemove", this.moveMouse);
    this.setState(() => {
      return {
        dragTaskId: taskId,
        dragType: dragType,
      };
    });
  };

  onEndDrag = (taskId: number, viewIndex: number, dragType: string, extraData: number, isCancelled: boolean) => {
    if (this.state.dragTaskId === null || this.state.dragType !== dragType) {
      return;
    }
    if (!isCancelled) {
      switch (dragType) {
        case DRAG_LINK:
          this.endLinkDrag(taskId, viewIndex);
          break;
        case DRAG_MOVE:
          this.endMoveDrag(taskId, viewIndex, extraData);
          break;
        case DRAG_RESIZE_START:
          this.endResizeDragStart(taskId, viewIndex, extraData);
          break;
        case DRAG_RESIZE_END:
          this.endResizeDragEnd(taskId, viewIndex, extraData);
          break;
        default:
          console.error("INVALID DRAG TYPE: ", dragType);
          break;
      }
    }

    this.clearDragData();
  };

  endResizeDragStart = (taskId: number, viewIndex: number, xDelta: number) => {
    const task = this.props.viewData[viewIndex][taskId];
    const objectTypeConfig        = this.props.getObjectTypeConfig(task.objectType);
    const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
    const hourWidth = this.getHourWidth();
    const snappedMoveDelta = snapMouseMoveBasedOnZoomMode(xDelta, this.props.zoomMode, hourWidth);
    const minimumDuration = getChildrenMinimumDurationInHours(this.props.viewData[viewIndex], task);
    const maximumStartTime = subHours(validEnd, minimumDuration);

    const startAdjustmentHoursRequested = snappedMoveDelta / hourWidth;
    let wantedStart;
    if (this.props.zoomMode !== ZOOM_MODE_DAY) {
      // Snap the resize into full days
      const dayDelta = Math.round(startAdjustmentHoursRequested / 24); // Math round because there might be daylight savings garbage involved
      wantedStart = addDays(validStart, dayDelta);
      if (wantedStart > maximumStartTime) {
        // differenceInDays counts only whole days, so if the hour counts don't match, add an additional day.
        const potentialExtraDay = getHours(maximumStartTime) === getHours(wantedStart) ? 0 : 1;
        wantedStart = subDays(wantedStart, differenceInDays(wantedStart, maximumStartTime) + potentialExtraDay);
      }
    } else {
      wantedStart = new Date(Math.min(Number(addHours(validStart, startAdjustmentHoursRequested)), Number(maximumStartTime)));
    }

    const updateDict = this.getTaskAndDependentsMoves(viewIndex, task, wantedStart, validEnd);
    this.props.moveOrResizeMany(updateDict);
  };

  endResizeDragEnd = (taskId: number, viewIndex: number, xDelta: number) => {
    const task = this.props.viewData[viewIndex][taskId];
    const objectTypeConfig        = this.props.getObjectTypeConfig(task.objectType);
    const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
    const hourWidth = this.getHourWidth();
    const snappedMoveDelta = snapMouseMoveBasedOnZoomMode(xDelta, this.props.zoomMode, hourWidth);
    const minimumDuration = getChildrenMinimumDurationInHours(this.props.viewData[viewIndex], task);
    const minimumEndTime = addHours(task.start, minimumDuration);

    const endAdjustmentHoursRequested = snappedMoveDelta / hourWidth;
    let wantedEnd: Date;
    if (this.props.zoomMode !== ZOOM_MODE_DAY) {
      // Snap the resize into full days
      const dayDelta = Math.round(endAdjustmentHoursRequested / 24); // Math round because there might be daylight savings garbage involved
      wantedEnd = addDays(validEnd, dayDelta);
      if (minimumEndTime > wantedEnd) {
        // differenceInDays counts only whole days, so if the hour counts don't match, add an additional day.
        const potentialExtraDay = getHours(minimumEndTime) === getHours(wantedEnd) ? 0 : 1;
        wantedEnd = addDays(wantedEnd, differenceInDays(minimumEndTime, wantedEnd) + potentialExtraDay);
      }
    } else {
      wantedEnd = new Date(Math.max(Number(addHours(validEnd, endAdjustmentHoursRequested)), Number(minimumEndTime)));
    }

    const updateDict = this.getTaskAndDependentsMoves(viewIndex, task, validStart, wantedEnd);
    this.props.moveOrResizeMany(updateDict);
  };

  endMoveDrag = (taskId: number, viewIndex: number, xDelta: number) => {
    const task = this.props.viewData[viewIndex][taskId];
    const objectTypeConfig        = this.props.getObjectTypeConfig(task.objectType);
    const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
    const hourWidth = this.getHourWidth();
    const snappedMoveDelta = snapMouseMoveBasedOnZoomMode(xDelta, this.props.zoomMode, hourWidth);
    const hourAdjustment = snappedMoveDelta / hourWidth;
    let newStart: Date;
    let newEnd: Date;

    if (this.props.zoomMode !== ZOOM_MODE_DAY) {
      const dayDelta = Math.round(hourAdjustment / 24); // Math round because there might be daylight savings garbage involved
      newStart = addDays(validStart, dayDelta);
      newEnd = addDays(validEnd, dayDelta);
      if (this.props.displaySettings.adjustForWeekendAndHolidays) {
        const adjustedTimes = findWorkingDayAdjustedTimeRange(task, newStart, newEnd, true);
        newStart = adjustedTimes.newStart;
        newEnd = adjustedTimes.newEnd;
      }
    } else {
      newStart = addHours(validStart, hourAdjustment);
      newEnd = addHours(validEnd, hourAdjustment);
    }
    const updateDict = this.getTaskAndDependentsMoves(viewIndex, task, newStart, newEnd);
    this.props.moveOrResizeMany(updateDict);
  };

  endLinkDrag = (endTaskId: number, viewIndex: number) => {
    if (this.state.dragTaskId === endTaskId) {
      return;
    }

    const dragStartTask = this.props.viewData[viewIndex][this.state.dragTaskId];
    const dragEndTask = this.props.viewData[viewIndex][endTaskId];

    // Check if link already exists and delete if so.
    for(const linkToTask of dragStartTask.linksTo) {
      if (linkToTask === endTaskId) {
        this.props.toggleLink(dragStartTask.id, endTaskId);
        return;
      }
    }

    const linkedTaskIds = getLinkedTaskIdsRecursive(dragEndTask, new Set(), this.props.viewData[viewIndex]);
    if (linkedTaskIds.has(dragStartTask.id)) {
      const { t } = this.props;
      this.props.showMessage(t("error.circulardependency"), NOTIFY);
      return; //This new link would've caused a circular dependency.
    }

    const startTaskobjectTypeConfig = this.props.getObjectTypeConfig(dragStartTask.objectType);
    const endTaskobjectTypeConfig = this.props.getObjectTypeConfig(dragEndTask.objectType);

    const [_validStartTaskStart, validStartTaskEnd] = ensureStartAndEnd(dragStartTask.start, dragStartTask.end, startOfDay(Date.now()), dragStartTask.duration, startTaskobjectTypeConfig.durationUnit);
    const [validEndTaskStart, validEndTaskEnd] = ensureStartAndEnd(dragEndTask.start, dragEndTask.end, startOfDay(Date.now()), dragEndTask.duration, endTaskobjectTypeConfig.durationUnit);

    if (validStartTaskEnd > validEndTaskStart) {
      //The task being linked to would start earlier than the link origin task would end. Move the dependent task to start later.
      const moveDuration = differenceInHours(validStartTaskEnd, validEndTaskStart);
      const newStart = addHours(validEndTaskStart, moveDuration);
      const newEnd = addHours(validEndTaskEnd, moveDuration);
      const updateDict = this.getTaskAndDependentsMoves(viewIndex, dragEndTask, newStart, newEnd);
      this.props.moveOrResizeMany(updateDict);
    }

    // Add link since it didn't exist already.
    this.props.toggleLink(dragStartTask.id, endTaskId);
  };

  render() {
    const hourWidth = this.getHourWidth();
    const greyDaysNumber = zoomModeInfo[this.props.zoomMode].extraContextDays;

    let startDateExtended = addDays(this.props.startDate, -greyDaysNumber);
    // The dates are compared to the previous dates to prevent unnecessary renders due to changing object reference.
    if (isEqual(startDateExtended, this.prevStartDate)) {
      startDateExtended = this.prevStartDate;
    }
    this.prevStartDate = startDateExtended;

    // + 1 comes from the fact that the endDate timestamp is at the start of the day
    // when the whole day should be included.
    let endDateExtended = addDays(this.props.endDate, greyDaysNumber + 1);
    if (isEqual(endDateExtended, this.prevEndDate)) {
      endDateExtended = this.prevEndDate;
    }
    this.prevEndDate = endDateExtended;

    const headerElem = this.reRenderHeader(
      startDateExtended,
      endDateExtended,
      hourWidth,
      this.props.zoomMode,
      this.props.displaySettings.taskHeight
    );
    return (
      <DndProvider backend={HTML5Backend}>
        <div className="app-container">
          <TimeLineHub
            viewData={this.props.viewData}
            viewConfigurations={this.props.viewConfigurations}
            dragTaskId={this.state.dragTaskId}
            dragType={this.state.dragType}
            mouseXDelta={this.state.mouseXDelta}
            mouseYDelta={this.state.mouseYDelta}
            hourWidth={hourWidth}
            zoomMode={this.props.zoomMode}
            onStartDrag={this.onStartDrag}
            onEndDrag={this.onEndDrag}
            msalContext={this.props.msalContext}
            //onTaskListSizing={this.onTaskListSizing}
            onHorizontalResize={this.onHorizontalResize}
            startDate={startDateExtended}
            endDate={endDateExtended}
            //sideBarWidth={this.state.sideBarWidth}
            headerElem={headerElem}
            viewPortWidth={this.state.viewPortWidth}
            loading={this.props.loading}
            dayClicked={this.props.dayClicked}
            onScrollToClickedDayEnd={this.props.clearDayClickedState}
          />
          {this.props.showTaskEditBox ? (
            <EditItemForm />
          ) : null}
          {this.props.showNewTaskBox ? (
            <AddItemForm />
          ) : null}
          {this.props.showConfigurationForm ? (
            <ConfigurationForm />
          ) : null}
          {this.props.showTokenForm ? (
            <TokenForm />
          ) : null}
          {this.props.showUIConfigForm ? (
            <UIConfigForm />
          ) : null}
        </div>
        {this.props.modalContents ? (
          <div>
            <ModalContainer {...this.props.modalStyles}>{this.props.modalContents}</ModalContainer>{" "}
            <ModalBackdrop onClick={this.props.hideModal}></ModalBackdrop>
          </div>
        ) : null}
      </DndProvider>
    );
  }
}

export default withTranslation()(withMsal(connect(
  (rootState: iRootState) => ({
    allData: rootState.items.allData,
    startDate: rootState.items.dateRange[0],
    endDate: rootState.items.dateRange[1],
    viewData: hierarchicalDataForViewsSelector(rootState as never),
    viewConfigurations: rootState.common.configuration.viewConfigurations,
    loading: rootState.items.loading,
    displaySettings: rootState.UI.displaySettings,
    zoomMode: rootState.UI.zoomMode,
    modalContents: rootState.UI.modalContents,
    modalStyles: rootState.UI.modalStyles,
    showTaskEditBox: rootState.UI.showTaskEditBox,
    showNewTaskBox: rootState.UI.showNewTaskBox,
    showConfigurationForm: rootState.UI.showConfigurationForm,
    showTokenForm: rootState.UI.showTokenForm,
    showUIConfigForm: rootState.UI.showUIConfigForm,
    dayClicked: rootState.UI.dayClicked,
    getObjectTypeConfig: (objectTypeId: number) => Array.from(rootState.common.configuration.objectTypeConfigurations.values()).find(i => i.objectTypeId === objectTypeId),
  }),
  (dispatch: Dispatch) => ({
    moveOrResizeMany: (updateDict: IMoveOrResizeManyPayload) => dispatch.items.moveOrResizeManyAsync(updateDict),
    toggleLink: (linkFromId: number, linkToId: number) => dispatch.items.toggleLink({ linkFromId, linkToId }),
    showMessage: dispatch.UI.showMessage,
    hideModal: dispatch.UI.hideModal,
    refreshItemsAsync: dispatch.items.refreshItemsAsync,
    clearDayClickedState: () => {
      dispatch.UI.setDayClicked( null );
    },
  })
)(App)));
